import type { TokenCredential } from "@azure/identity";
import type { AgentDefinition } from "./foundry-agent-get.js";
import { LocalCliError } from "./errors.js";

const FOUNDRY_DATA_SCOPE = "https://ai.azure.com/.default";

/**
 * POST a new Foundry agent version via raw REST. Shared by a2a-enable,
 * brain-link (and future callers) — the request shape must stay
 * byte-identical to what those callers' tests already assert on.
 */
export async function createAgentVersion(
  args: { credential: TokenCredential; projectEndpoint: string; agentName: string; definition: AgentDefinition; metadata: Record<string, string>; extraHeaders?: Record<string, string> },
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const token = await args.credential.getToken(FOUNDRY_DATA_SCOPE);
  if (!token?.token) throw new LocalCliError({ code: "FOUNDRY_AUTH", message: "Could not acquire Foundry data-plane token" });
  const url = `${args.projectEndpoint}/agents/${args.agentName}/versions?api-version=v1`;
  const res = await fetchImpl(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token.token}`, "Content-Type": "application/json", ...(args.extraHeaders ?? {}) },
    body: JSON.stringify({ definition: args.definition, metadata: args.metadata }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({ code: "AGENT_CREATE_VERSION_FAILED", message: `POST ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 500)}` });
  }
  const data = (await res.json()) as { version?: string };
  if (!data.version) throw new LocalCliError({ code: "AGENT_CREATE_VERSION_NO_VERSION", message: "createVersion returned no version" });
  return data.version;
}
